import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, ArrowUpRight } from "lucide-react";
import { PageHero, CtaBand } from "@/components/site/sections";
import { Reveal } from "@/components/site/ui";
import { fetchBlogPosts } from "@/lib/api";
import { IMAGES } from "@/data/content";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const fallbackCovers = [
  IMAGES.kitchen1,
  IMAGES.interior1,
  IMAGES.wardrobe1,
  IMAGES.renovation,
  IMAGES.kitchen2,
  IMAGES.interior2,
];

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchBlogPosts();
        setPosts(data);
      } catch (e) {
        setError("Unable to load posts");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const [featured, ...rest] = posts;

  return (
    <>
      <PageHero
        overline="Journal"
        title="Ideas, guides & inspiration for a better home."
        subtitle="Practical guides on modular kitchens, wardrobes, interiors and renovation — written by the Spherio Living design team."
        image={IMAGES.interior2}
      />

      <section
        data-testid="blog-list-section"
        className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32"
      >
        {loading && (
          <div className="text-center text-[#5C574F]" data-testid="blog-loading">
            Loading posts…
          </div>
        )}

        {!loading && error && (
          <div className="text-center text-red-600" data-testid="blog-error">
            {error}
          </div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div
            className="text-center text-[#5C574F] py-12"
            data-testid="blog-empty"
          >
            <p className="font-head text-2xl text-[#1C1A17]">
              New stories are on the way.
            </p>
            <p className="mt-3">
              Our design team is putting the finishing touches on the first
              journal entries. Please check back soon.
            </p>
          </div>
        )}

        {!loading && featured && (
          <Reveal>
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`blog-featured-${featured.slug}`}
              className="group grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 rounded-3xl overflow-hidden bg-[#F5F1EA]"
            >
              <div className="relative aspect-[4/3] lg:aspect-auto lg:h-full overflow-hidden">
                <img
                  src={featured.cover_image || fallbackCovers[0]}
                  alt={featured.title}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col justify-center p-8 lg:pr-14 lg:py-14">
                <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#B8935A]">
                  Featured
                </span>
                <h2 className="font-head text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#1C1A17] mt-4">
                  {featured.title}
                </h2>
                {featured.excerpt && (
                  <p className="mt-5 text-[#5C574F] text-base lg:text-lg leading-relaxed max-w-xl">
                    {featured.excerpt}
                  </p>
                )}
                <div className="mt-6 flex items-center gap-4 text-sm text-[#5C574F]">
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {formatDate(featured.created_at)}
                  </span>
                  <span>·</span>
                  <span>{featured.author}</span>
                </div>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#1C1A17] group-hover:text-[#B8935A] transition-colors">
                  Read the article
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </Reveal>
        )}

        {!loading && rest.length > 0 && (
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rest.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group flex flex-col h-full"
                >
                  <div className="relative aspect-[4/3] overflow-hidden rounded-2xl">
                    <img
                      src={
                        p.cover_image ||
                        fallbackCovers[(i + 1) % fallbackCovers.length]
                      }
                      alt={p.title}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-6 flex flex-col flex-1">
                    {p.tags?.[0] && (
                      <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#B8935A]">
                        {p.tags[0]}
                      </span>
                    )}
                    <h3 className="font-head text-2xl font-medium tracking-tight text-[#1C1A17] mt-3 group-hover:text-[#B8935A] transition-colors">
                      {p.title}
                    </h3>
                    {p.excerpt && (
                      <p className="mt-3 text-[#5C574F] text-sm leading-relaxed line-clamp-3">
                        {p.excerpt}
                      </p>
                    )}
                    <div className="mt-5 flex items-center gap-3 text-xs text-[#5C574F]">
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays className="h-3 w-3" />
                        {formatDate(p.created_at)}
                      </span>
                      <span>·</span>
                      <span>{p.author}</span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <CtaBand />
    </>
  );
}
